import type { ReactElement } from 'react';
import type { FeatureFlag } from '@/features/feature-flags';
import type { PermissionKey } from './hooks';
import type { PlatformOs } from './gating';
import { SETTING_KINDS, type SettingKey, type SettingKind } from './setting-kinds';

/**
 * The settings registry: a single declarative catalog of everything the settings
 * screen shows. Each entry declares PRESENTATION (section, order, label, icon)
 * and GATING (platform, flag, dependsOnPermission), and is one of three kinds:
 *
 *  - `control`  — bound to a schema key, rendered generically (toggle / select).
 *                 `kind` MUST match `SETTING_KINDS[settingKey]` (enforced at the
 *                 type level by the `control()` builder below).
 *  - `custom`   — renders its own component or routes to a bespoke screen.
 *  - `permission` — a status-aware row backed by `usePermission`.
 *
 * The value schemas (AccountSettingsSchema / DeviceSettingsSchema) remain the
 * source of truth for a setting's type and default; the registry NEVER redeclares
 * field types.
 */

export type SettingSection =
  | 'account'
  | 'preferences'
  | 'notifications'
  | 'permissions'
  | 'support';

/** The order sections are rendered in, and their human titles. */
export const SECTION_ORDER: SettingSection[] = [
  'account',
  'preferences',
  'notifications',
  'permissions',
  'support',
];

export const SECTION_TITLES: Record<SettingSection, string> = {
  account: 'Account',
  preferences: 'Preferences',
  notifications: 'Notifications',
  permissions: 'Permissions',
  support: 'Support',
};

/** Fields shared by every entry: presentation + gating. */
interface BaseEntry {
  section: SettingSection;
  order: number;
  label: string;
  icon: string;
  platform?: readonly PlatformOs[];
  flag?: FeatureFlag;
  dependsOnPermission?: PermissionKey;
}

export interface SelectOption {
  label: string;
  value: string;
}

export type ControlKind = 'toggle' | 'select';

export interface ControlEntry extends BaseEntry {
  type: 'control';
  kind: SettingKind;
  control: ControlKind;
  settingKey: SettingKey;
  options?: SelectOption[];
}

export interface CustomEntry extends BaseEntry {
  type: 'custom';
  id: string;
  route?: string;
  render?: () => ReactElement | null;
  destructive?: boolean;
}

export interface PermissionEntry extends BaseEntry {
  type: 'permission';
  permissionKey: PermissionKey;
}

export type SettingsEntry = ControlEntry | CustomEntry | PermissionEntry;

/**
 * Typed builder for a control entry. Generic over the setting key, so:
 *  - an invalid `settingKey` is a compile error (must be a real `SettingKey`), and
 *  - `kind` is pinned to `SETTING_KINDS[settingKey]` — declaring the wrong kind
 *    fails to compile.
 */
function control<K extends SettingKey>(entry: {
  section: SettingSection;
  order: number;
  label: string;
  icon: string;
  control: ControlKind;
  settingKey: K;
  kind: (typeof SETTING_KINDS)[K];
  options?: SelectOption[];
  platform?: readonly PlatformOs[];
  flag?: FeatureFlag;
  dependsOnPermission?: PermissionKey;
}): ControlEntry {
  return { type: 'control', ...entry };
}

const UNIT_OPTIONS: SelectOption[] = [
  { label: 'Imperial', value: 'imperial' },
  { label: 'Metric', value: 'metric' },
];

const LANDING_OPTIONS: SelectOption[] = [
  { label: 'Discover', value: 'discover' },
  { label: 'Activity', value: 'activity' },
];

export const SETTINGS_REGISTRY: SettingsEntry[] = [
  // --- Account ---------------------------------------------------------------
  {
    type: 'custom',
    id: 'account-info',
    section: 'account',
    order: 0,
    label: 'Account Information',
    icon: 'person-outline',
    flag: 'accountInformation',
  },
  {
    type: 'custom',
    id: 'taste-profile',
    section: 'account',
    order: 1,
    label: 'Taste Profile',
    icon: 'restaurant-outline',
    route: '/(root)/taste-profile',
  },
  {
    type: 'custom',
    id: 'security',
    section: 'account',
    order: 2,
    label: 'Sign-in & Security',
    icon: 'lock-closed-outline',
    route: '/(root)/security',
  },
  {
    type: 'custom',
    id: 'delete-account',
    section: 'account',
    order: 3,
    label: 'Delete Account',
    icon: 'trash-outline',
    route: '/(root)/delete-account',
    destructive: true,
  },

  // --- Preferences -----------------------------------------------------------
  control({
    section: 'preferences',
    order: 0,
    label: 'Units',
    icon: 'speedometer-outline',
    control: 'select',
    kind: 'account',
    settingKey: 'units',
    options: UNIT_OPTIONS,
  }),
  control({
    section: 'preferences',
    order: 1,
    label: 'Default Landing Screen',
    icon: 'home-outline',
    control: 'select',
    kind: 'account',
    settingKey: 'defaultLandingScreen',
    options: LANDING_OPTIONS,
  }),
  control({
    section: 'preferences',
    order: 2,
    label: 'Reduce Motion',
    icon: 'accessibility-outline',
    control: 'toggle',
    kind: 'device',
    settingKey: 'reduceMotion',
    flag: 'reduceMotion',
  }),

  // --- Notifications (stored only; hidden behind notificationSettings) ------
  control({
    section: 'notifications',
    order: 0,
    label: 'Push Notifications',
    icon: 'notifications-outline',
    control: 'toggle',
    kind: 'account',
    settingKey: 'pushEnabled',
    flag: 'notificationSettings',
  }),
  control({
    section: 'notifications',
    order: 1,
    label: 'Email Notifications',
    icon: 'mail-outline',
    control: 'toggle',
    kind: 'account',
    settingKey: 'emailEnabled',
    flag: 'notificationSettings',
  }),
  control({
    section: 'notifications',
    order: 2,
    label: 'Friend Activity',
    icon: 'people-outline',
    control: 'toggle',
    kind: 'account',
    settingKey: 'friendActivity',
    flag: 'notificationSettings',
  }),
  control({
    section: 'notifications',
    order: 3,
    label: 'New Followers',
    icon: 'person-add-outline',
    control: 'toggle',
    kind: 'account',
    settingKey: 'newFollowers',
    flag: 'notificationSettings',
  }),
  control({
    section: 'notifications',
    order: 4,
    label: 'Recommendations',
    icon: 'sparkles-outline',
    control: 'toggle',
    kind: 'account',
    settingKey: 'recommendations',
    flag: 'notificationSettings',
  }),
  control({
    section: 'notifications',
    order: 5,
    label: 'Review Reminders',
    icon: 'star-outline',
    control: 'toggle',
    kind: 'account',
    settingKey: 'reviewReminders',
    flag: 'notificationSettings',
  }),

  // --- Permissions -----------------------------------------------------------
  {
    type: 'permission',
    permissionKey: 'location',
    section: 'permissions',
    order: 0,
    label: 'Location',
    icon: 'location-outline',
  },

  // --- Support ---------------------------------------------------------------
  {
    type: 'custom',
    id: 'help-center',
    section: 'support',
    order: 0,
    label: 'Help Center',
    icon: 'help-circle-outline',
    flag: 'helpCenter',
  },
  {
    type: 'custom',
    id: 'about',
    section: 'support',
    order: 1,
    label: 'About',
    icon: 'information-circle-outline',
    flag: 'about',
  },
  {
    type: 'custom',
    id: 'terms-of-service',
    section: 'support',
    order: 2,
    label: 'Terms of Service',
    icon: 'document-text-outline',
    route: '/(root)/terms-of-service',
  },
  {
    type: 'custom',
    id: 'privacy-policy',
    section: 'support',
    order: 3,
    label: 'Privacy Policy',
    icon: 'shield-checkmark-outline',
    route: '/(root)/privacy-policy',
  },
];

/** Entries belonging to a section, sorted by their declared order. */
export function entriesForSection(section: SettingSection): SettingsEntry[] {
  return SETTINGS_REGISTRY.filter((e) => e.section === section).sort(
    (a, b) => a.order - b.order,
  );
}
